class DecoraCatalog extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector("[data-decora-grid]");
    this.count = this.querySelector("[data-decora-count]");
    this.search = this.querySelector("[data-decora-search]");
    this.paginations = Array.from(this.querySelectorAll("[data-decora-pagination]"));
    this.filters = Array.from(this.querySelectorAll("[data-decora-filter]"));
    this.categoryMedia = Array.from(this.querySelectorAll("[data-decora-category-media]"));
    this.products = [];
    this.filteredProducts = [];
    this.activeFilter = "all";
    this.currentPage = 1;
    this.pageSize = Number(this.dataset.pageSize || 24);

    this.bindEvents();
    this.loadProducts();
  }

  bindEvents() {
    this.search?.addEventListener("input", () => {
      this.currentPage = 1;
      this.renderProducts();
    });

    this.filters.forEach((button) => {
      button.addEventListener("click", () => {
        this.activeFilter = button.dataset.decoraFilter || "all";
        this.currentPage = 1;
        this.filters.forEach((item) => {
          item.setAttribute("aria-pressed", String(item.dataset.decoraFilter === this.activeFilter));
        });
        this.renderProducts();
      });
    });
  }

  async loadProducts() {
    this.setLoading();
    const endpoint = this.dataset.endpoint || "/products.json";
    const limit = Number(this.dataset.limit || 250);
    let page = 1;
    let products = [];

    while (true) {
      const url = `${endpoint}${endpoint.includes("?") ? "&" : "?"}limit=${limit}&page=${page}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Product feed failed with ${response.status}`);
      }

      const payload = await response.json();
      const rows = Array.isArray(payload.products) ? payload.products : [];
      products = products.concat(rows);

      if (rows.length < limit) break;
      page += 1;
    }

    this.products = products.map((product) => this.normalizeProduct(product));
    this.renderCategoryMedia();
    this.renderProducts();
  }

  normalizeProduct(product) {
    const firstVariant = product.variants?.[0] || {};
    const firstImage = product.images?.[0] || {};
    const title = product.title || "";
    const variantTitle = firstVariant.title || "";
    const imageUrl = firstImage.src || "";
    const searchable = this.stripHtml(`${title} ${product.body_html || ""} ${product.product_type || ""}`);
    const kind = this.inferKind(title, variantTitle);

    return {
      id: product.id,
      title,
      handle: product.handle,
      image: imageUrl,
      previewImage: this.imageWithWidth(imageUrl, 720),
      previewSrcset: this.imageSrcset(imageUrl, [420, 560, 720, 900]),
      categoryImage: this.imageWithWidth(imageUrl, 900),
      price: Number(firstVariant.price || 0),
      compareAtPrice: Number(firstVariant.compare_at_price || 0),
      kind,
      familyKey: this.familyKey(title, product.handle),
      hasText: this.hasTextOverlay(product, imageUrl, searchable),
      searchable: searchable.toLowerCase(),
      type: product.product_type || "Cuadro cristiano",
    };
  }

  familyKey(title, handle) {
    const normalizedTitle = String(title || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+-\s+(?:p\d+\s*-\s*)?c\d+\s*$/i, "")
      .replace(/\s+-\s+panel\s+\d+\s*$/i, "")
      .replace(/\s+/g, " ")
      .trim();

    return normalizedTitle || String(handle || "");
  }

  mixCatalogProducts(products) {
    const queues = [];
    const grouped = new Map();

    products.forEach((product) => {
      const key = `${product.hasText ? "text" : "image"}-${product.kind}`;

      if (!grouped.has(key)) {
        const queue = { key, items: [] };
        grouped.set(key, queue);
        queues.push(queue);
      }

      grouped.get(key).items.push(product);
    });

    queues.forEach((queue) => {
      queue.items.sort((left, right) => this.stableScore(left.handle || left.title) - this.stableScore(right.handle || right.title));
    });

    const ordered = [];

    while (queues.some((queue) => queue.items.length)) {
      const previousKey = ordered.length ? ordered[ordered.length - 1].familyKey : "";
      const previousGroup = ordered.length
        ? `${ordered[ordered.length - 1].hasText ? "text" : "image"}-${ordered[ordered.length - 1].kind}`
        : "";
      let queueIndex = queues.findIndex((queue) => {
        const next = queue.items[0];
        return next && queue.key !== previousGroup && next.familyKey !== previousKey;
      });

      if (queueIndex === -1) {
        queueIndex = queues.findIndex((queue) => queue.items.length && queue.items[0].familyKey !== previousKey);
      }

      if (queueIndex === -1) {
        queueIndex = queues.findIndex((queue) => queue.items.length);
      }

      const queue = queues.splice(queueIndex, 1)[0];
      ordered.push(queue.items.shift());
      queues.push(queue);
    }

    return ordered;
  }

  stableScore(value) {
    return String(value || "").split("").reduce((hash, char) => {
      return ((hash << 5) - hash + char.charCodeAt(0)) >>> 0;
    }, 2166136261);
  }

  inferKind(title, variantTitle) {
    const source = `${title} ${variantTitle}`
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (source.includes("triptico") || source.includes("set de 3")) {
      return "triptych";
    }

    const dimensions = variantTitle.match(/(\d+)\s*x\s*(\d+)/i);
    if (dimensions) {
      const width = Number(dimensions[1]);
      const height = Number(dimensions[2]);
      if (width > height) return "horizontal";
    }

    return "vertical";
  }

  hasTextOverlay(product, imageUrl, searchable) {
    const source = `${imageUrl} ${product.handle || ""} ${product.tags || ""} ${searchable}`.toLowerCase();

    if (source.includes("text_overlay")) return true;
    if (source.includes("text-overlay")) return true;
    if (source.includes("texto agregado")) return true;
    if (source.includes("con texto")) return true;

    return false;
  }

  renderProducts() {
    const query = (this.search?.value || "").trim().toLowerCase();
    const products = this.products.filter((product) => {
      const matchesSearch = !query || product.searchable.includes(query);
      const matchesFilter =
        this.activeFilter === "all" ||
        (this.activeFilter === "image" && !product.hasText) ||
        (this.activeFilter === "scripture" && product.hasText) ||
        product.kind === this.activeFilter;

      return matchesSearch && matchesFilter;
    });

    const orderedProducts = this.mixCatalogProducts(products);
    this.filteredProducts = orderedProducts;
    const totalPages = Math.max(1, Math.ceil(orderedProducts.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, totalPages);
    const start = (this.currentPage - 1) * this.pageSize;
    const visibleProducts = orderedProducts.slice(start, start + this.pageSize);

    this.count.textContent = orderedProducts.length
      ? `Mostrando ${start + 1}-${start + visibleProducts.length} de ${orderedProducts.length} cuadros`
      : `0 de ${this.products.length || 0} cuadros`;

    if (!orderedProducts.length) {
      this.grid.innerHTML = `<p class="decora-home__empty">No encontramos cuadros con esa búsqueda.</p>`;
      this.renderPagination(totalPages);
      return;
    }

    this.grid.innerHTML = visibleProducts.map((product) => this.renderCard(product)).join("");
    this.renderPagination(totalPages);
  }

  renderCategoryMedia() {
    this.categoryMedia.forEach((media) => {
      const key = media.dataset.decoraCategoryMedia;
      const product = this.products.find((item) => {
        if (!item.image) return false;
        if (key === "scripture") return item.hasText;
        return !item.hasText;
      });

      if (!product) return;

      media.innerHTML = `<img src="${this.escape(product.categoryImage || product.image)}" alt="${this.escape(product.title)}" loading="lazy" decoding="async">`;
    });
  }

  renderPagination(totalPages) {
    if (!this.paginations.length) return;

    if (!this.filteredProducts.length || totalPages <= 1) {
      this.paginations.forEach((pagination) => {
        pagination.hidden = true;
        pagination.innerHTML = "";
      });
      return;
    }

    const pages = this.visiblePages(totalPages);
    const paginationHtml = `
      <button class="decora-home__page-button" type="button" data-page-action="prev" ${this.currentPage === 1 ? "disabled" : ""}>Anterior</button>
      ${pages.map((page) => `
        ${page === "gap" ? '<span class="decora-home__page-status">...</span>' : `<button class="decora-home__page-button" type="button" data-page-number="${page}" aria-current="${page === this.currentPage ? "page" : "false"}">${page}</button>`}
      `).join("")}
      <button class="decora-home__page-button" type="button" data-page-action="next" ${this.currentPage === totalPages ? "disabled" : ""}>Siguiente</button>
      <button class="decora-home__more" type="button" data-page-action="next" ${this.currentPage === totalPages ? "disabled" : ""}>Ver más cuadros</button>
      <span class="decora-home__page-status">Página ${this.currentPage} de ${totalPages}</span>
    `;

    this.paginations.forEach((pagination) => {
      pagination.hidden = false;
      pagination.innerHTML = paginationHtml;

      pagination.querySelectorAll("[data-page-number]").forEach((button) => {
        button.addEventListener("click", () => this.goToPage(Number(button.dataset.pageNumber)));
      });

      pagination.querySelectorAll("[data-page-action]").forEach((button) => {
        button.addEventListener("click", () => {
          const action = button.dataset.pageAction;
          if (action === "prev") this.goToPage(this.currentPage - 1);
          if (action === "next") this.goToPage(this.currentPage + 1);
        });
      });
    });
  }

  visiblePages(totalPages) {
    const corePages = new Set([1, totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
    const pages = Array.from(corePages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
    return pages.flatMap((page, index) => {
      const previous = pages[index - 1];
      return previous && page - previous > 1 ? ["gap", page] : [page];
    });
  }

  goToPage(page) {
    const totalPages = Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize));
    this.currentPage = Math.min(Math.max(1, page), totalPages);
    this.renderProducts();
    this.querySelector(".decora-home__catalog-head")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  renderCard(product) {
    const image = product.image
      ? `<img src="${this.escape(product.previewImage || product.image)}" ${product.previewSrcset ? `srcset="${this.escape(product.previewSrcset)}"` : ""} sizes="(max-width: 560px) 44vw, (max-width: 900px) 44vw, (max-width: 1180px) 30vw, 24vw" alt="${this.escape(product.title)}" loading="lazy" decoding="async">`
      : "";
    const compare = product.compareAtPrice > product.price
      ? `<span class="decora-card__compare">${this.money(product.compareAtPrice)}</span>`
      : "";

    return `
      <article class="decora-card" data-kind="${product.kind}">
        <a class="decora-card__media" href="/products/${this.escape(product.handle)}" aria-label="${this.escape(product.title)}">
          ${image}
          <span class="decora-card__badge">Oferta</span>
        </a>
        <div class="decora-card__body">
          <h3 class="decora-card__title">
            <a href="/products/${this.escape(product.handle)}">${this.escape(product.title)}</a>
          </h3>
          <div class="decora-card__footer">
            <span class="decora-card__price">${compare}<span>A partir de ${this.money(product.price)}</span></span>
          </div>
        </div>
      </article>
    `;
  }

  setLoading() {
    this.grid.innerHTML = `<p class="decora-home__loading">Cargando catálogo...</p>`;
    this.count.textContent = "Cargando";
    this.paginations.forEach((pagination) => {
      pagination.hidden = true;
    });
  }

  kindLabel(kind) {
    if (kind === "horizontal") return "Horizontal";
    if (kind === "triptych") return "Tríptico";
    return "Vertical";
  }

  money(value) {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(value);
  }

  imageWithWidth(src, width) {
    if (!src) return "";

    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch {
      return src;
    }
  }

  imageSrcset(src, widths) {
    if (!src) return "";

    return widths
      .map((width) => `${this.imageWithWidth(src, width)} ${width}w`)
      .join(", ");
  }

  stripHtml(value) {
    const template = document.createElement("template");
    template.innerHTML = value;
    return template.content.textContent || "";
  }

  escape(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

customElements.define("decora-catalog", DecoraCatalog);

function openDecoraHashTarget() {
  if (!window.location.hash) return;

  const id = decodeURIComponent(window.location.hash.slice(1));
  const target = document.getElementById(id);
  if (!target) return;

  if (target.tagName.toLowerCase() === "details") {
    target.open = true;
  }

  window.setTimeout(() => {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 80);
}

window.addEventListener("hashchange", openDecoraHashTarget);
document.addEventListener("DOMContentLoaded", openDecoraHashTarget);
